import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { DatePicker } from 'components/DatePicker';
import { cn, getMilestoneTag } from 'lib/utils';
import { UserAttributeData } from 'services/profile/type';
import useUpdateProfile from 'hooks/useUpdateProfile';
import useAuthenticate from 'hooks/useAuthenticate';
import {
  UserDetailsFormData,
  UserDetailsValidator,
} from 'lib/validators/user-details';
import { usePostOnboardingStep } from 'services/dashboard/hooks';
import { usePostUserAttributes } from 'services/profile/hooks';

const NUMERIC_REGEX = /^(?:[1-9][0-9]*)?$/;

export default function UserDetails() {
  const { t } = useTranslation();

  const { onUpdateUserAttributes } = useUpdateProfile();
  const { onUpdateMemberDetails } = useAuthenticate();

  const form = useForm<UserDetailsFormData>({
    resolver: zodResolver(UserDetailsValidator),
    defaultValues: {
      birthdate: undefined,
      gender: '',
      given_name: '',
      family_name: '',
      height: '',
      weight: '',
      height_unit: 'cm',
      weight_unit: 'kg',
    },
  });

  const { mutate, isPending } = usePostUserAttributes({
    onSuccess: async () => {
      const milestone = getMilestoneTag('user-detail');
      updateOnboarding({ milestone });

      const profileId = await onUpdateMemberDetails();
      await onUpdateUserAttributes(profileId);
    },
    onError: () => {
      toast.error(t('failed_to_update_details_msg'));
    },
  });

  const { mutate: updateOnboarding } = usePostOnboardingStep();

  function onSubmit(values: UserDetailsFormData) {
    const data = {
      ...values,
      birthdate: format(values.birthdate, 'dd/MM/yyyy'),
      middle_name: '',
    };
    mutate(data as UserAttributeData);
  }

  return (
    <div className='flex items-center justify-center w-full h-full py-8'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full max-w-2xl px-6 mx-auto space-y-6'
        >
          <div className='flex flex-col items-center w-full gap-8 sm:flex-row'>
            <FormField
              control={form.control}
              name='given_name'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{t('firstname')}</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='family_name'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{t('lastname')}</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='flex flex-col items-center gap-8 sm:flex-row'>
            <FormField
              control={form.control}
              name='birthdate'
              render={({ field }) => (
                <FormItem className='flex flex-col w-full'>
                  <FormLabel>{t('dob')}</FormLabel>

                  <FormControl>
                    <DatePicker
                      selected={field.value}
                      onSelect={(value: Date | undefined) =>
                        field.onChange(value)
                      }
                    >
                      <Button
                        variant='ghost'
                        className={cn(
                          'justify-start hover:bg-transparent border-b-2 border-b-primary rounded-none h-12 pb-0 relative',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? format(field.value, 'dd/MM/yyyy') : null}
                        <CalendarIcon className='absolute w-5 h-5 right-2' />
                      </Button>
                    </DatePicker>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='gender'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{t('gender')}</FormLabel>

                  <FormControl>
                    <Select
                      {...field}
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className='border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='male'>{t('male')}</SelectItem>
                        <SelectItem value='female'>{t('female')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='grid items-center w-full grid-cols-1 gap-8 sm:grid-cols-2 sm:flex-row'>
            <div className='flex items-end'>
              <FormField
                control={form.control}
                name='height'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('height')}</FormLabel>

                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          const regex = NUMERIC_REGEX;
                          if (regex.test(value)) {
                            field.onChange(value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='height_unit'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select
                        {...field}
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className='w-20 border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='cm'>cm</SelectItem>
                          <SelectItem value='ft'>ft</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className='flex items-end'>
              <FormField
                control={form.control}
                name='weight'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('weight')}</FormLabel>

                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          const regex = NUMERIC_REGEX;
                          if (regex.test(value)) {
                            field.onChange(value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='weight_unit'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select
                        {...field}
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className='w-20 border-b-2 border-transparent rounded-none focus:ring-transparent focus:ring-0 border-b-primary'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='kg'>kg</SelectItem>
                          <SelectItem value='lbs'>lbs</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className='flex justify-center !mt-10'>
            <Button
              type='submit'
              className='px-10 font-semibold rounded-full'
              disabled={!form.formState.isValid || isPending}
            >
              {isPending && (
                <Loader2 className='inline w-4 h-4 mr-2 animate-spin' />
              )}
              {t('save')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
